/**
 * Editorial section heading for lodging surfaces — an optional small-caps
 * eyebrow in the tenant accent, a serif display title, and a soft subtitle.
 *
 * The eyebrow uses `terra-dark` because both light and dark tenant palettes
 * expose their highest-contrast accent shade there (dark accent ramps are
 * inverted in site configs, so `terra-dark` maps to a light shade in dark
 * mode). `tone="inverse"` is for fixed-dark bands (e.g. direct_book_pitch)
 * where the copy sits on `bg-cta` in both modes.
 */
import { cn } from '@/lib/utils';

interface SectionHeadingProps {
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  align?: 'left' | 'center';
  /** Heading level — h2 on homepage sections, h1 on page headers. */
  as?: 'h1' | 'h2' | 'h3';
  /** 'inverse' renders light-on-dark for fixed-dark brand bands. */
  tone?: 'default' | 'inverse';
  /** 'lg' for page/homepage headings, 'md' for in-page section headings. */
  size?: 'md' | 'lg';
  className?: string;
}

export default function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  as: Tag = 'h2',
  tone = 'default',
  size = 'lg',
  className,
}: SectionHeadingProps) {
  if (!eyebrow && !title && !subtitle) return null;
  const inverse = tone === 'inverse';
  const centered = align === 'center';

  return (
    <div className={cn(centered && 'text-center', className)}>
      {eyebrow && (
        <p
          className={cn(
            'mb-3 text-xs font-semibold uppercase tracking-widest',
            inverse ? 'text-terra-light' : 'text-terra-dark',
          )}
        >
          {eyebrow}
        </p>
      )}
      {title && (
        <Tag
          className={cn(
            'font-display font-bold tracking-tight',
            size === 'lg' ? 'text-3xl md:text-4xl' : 'text-2xl',
            inverse ? 'text-white' : 'text-ink',
          )}
        >
          {title}
        </Tag>
      )}
      {subtitle && (
        <p
          className={cn(
            'mt-3 text-base md:text-lg',
            inverse ? 'text-white/85' : 'text-ink-soft',
            centered && 'mx-auto max-w-2xl',
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
